import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  MyLocation as TargetIcon,
  Star as StarIcon
} from '@mui/icons-material';
import ValueCard from '../../components/cards/ValueCard';
import SectionCard from '../../components/cards/SectionCard';
import { RestrictedToRole } from '../../roles/RestrictedToRole';
import { getAboutData, updateAboutData } from '../../services/aboutAPIs';

const MarabesAboutPage = () => {
  const theme = useTheme();
  
  // Brand colors
  const brandColors = {
    primary: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
    accent: '#66BB6A'
  };

  // State management
  const [pageData, setPageData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, type: '', index: null });
  const [newItemText, setNewItemText] = useState('');
  const [newItemTitle, setNewItemTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    getAboutData()
      .then((data) => {
        setPageData(data);
        setOriginalData(JSON.parse(JSON.stringify(data))); // Deep copy for cancel
      })
      .catch((err) => console.error('Failed to fetch about data', err));
  }, []);

  // Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Alex+Brush&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // Edit handlers
  const handleEdit = (type, index = null) => {
    setEditDialog({ open: true, type, index });
    if (type === 'coreValues' || type === 'whyJoin') {
      if (index !== null) {
        setNewItemTitle(pageData[type][index].title);
        setNewItemText(pageData[type][index].description);
      } else {
        setNewItemTitle('');
        setNewItemText('');
      }
    } else {
      setNewItemText(pageData[type]);
    }
  };

  // const handleSaveChanges = async () => {
  //   setIsSaving(true);
  //   try {
  //     await updateAboutData(pageData);
  //     setOriginalData(JSON.parse(JSON.stringify(pageData))); // Update original data
  //   } catch (err) {
  //     console.error("Failed to save changes", err);
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleCancelEdit = () => {
    setPageData(JSON.parse(JSON.stringify(originalData))); // Revert to original data
    setEditMode(false);
  };


  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      await updateAboutData(pageData);
      setOriginalData(JSON.parse(JSON.stringify(pageData))); // Update original data
      setEditMode(false); // Exit edit mode after saving
    } catch (err) {
      console.error("Failed to save changes", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const { type, index } = editDialog;

    try {
      if (type === 'coreValues' || type === 'whyJoin') {
        const updatedItems = [...pageData[type]];
        if (index !== null) {
          updatedItems[index] = { ...updatedItems[index], title: newItemTitle, description: newItemText };
        } else {
          updatedItems.push({ title: newItemTitle, description: newItemText });
        }
        const updatedPageData = { ...pageData, [type]: updatedItems };
        await updateAboutData(updatedPageData);
        setPageData(updatedPageData);
      } else {
        const updatedPageData = { ...pageData, [type]: newItemText };
        await updateAboutData(updatedPageData);
        setPageData(updatedPageData);
      }
    } catch (err) {
      console.error("Failed to save", err);
    } finally {
      setIsSaving(false);
      setEditDialog({ open: false, type: '', index: null });
      setNewItemText('');
      setNewItemTitle('');
    }
  };

  const handleDelete = async (type, index) => {
    const updatedItems = pageData[type].filter((_, i) => i !== index);
    const updatedPageData = { ...pageData, [type]: updatedItems };
    try {
      await updateAboutData(updatedPageData);
      setPageData(updatedPageData);
    } catch (err) {
      console.error("Failed to delete item", err);
    }
  };

  if (!pageData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      {/* Hero Section */}
      <Box sx={{ 
        background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.dark} 100%)`,
        color: 'white',
        py: 6,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '20px',
      }}>
        <Container maxWidth="lg">
          <Box textAlign="center" position="relative" zIndex={2}>
            <Typography variant="h3" sx={{ 
              fontFamily: '"Alex Brush", cursive',
              fontSize: '2rem',
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {pageData.heroText}
            </Typography>
            {editMode && (
              <IconButton onClick={() => handleEdit('heroText')} sx={{ 
                color: 'white', 
                mt: 2,
                position: 'absolute',
                right: '0%',
                bottom: '0%',
              }}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="100%" sx={{ py: 6 }}>
        {/* Content Sections */}
        <SectionCard 
          brandColors={brandColors}
          editMode={editMode}
          title="Our Story"
          content={pageData.briefHistory}
          icon={<BusinessIcon sx={{ color: brandColors.primary }} />}
          onEdit={() => handleEdit('briefHistory')}
        />

        <SectionCard 
          brandColors={brandColors}
          editMode={editMode}
          title="Our Mission"
          content={pageData.mission}
          icon={<TargetIcon sx={{ color: brandColors.primary }} />}
          onEdit={() => handleEdit('mission')}
        />

        <SectionCard 
          brandColors={brandColors}
          editMode={editMode}
          title="Our Commitment"
          content={pageData.commitment}
          icon={<StarIcon sx={{ color: brandColors.primary }} />}
          onEdit={() => handleEdit('commitment')}
        />

        {/* Core Values */}
        <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <Typography variant="h4" sx={{ color: brandColors.dark, fontWeight: 600 }}>
              Core Values
            </Typography>
            {editMode && (
              <Fab size="small" onClick={() => handleEdit('coreValues')} sx={{ 
                bgcolor: brandColors.primary,
                width: 32, height: 32,
                minWidth: 32, minHeight: 32,
                fontSize: 12, marginLeft: 'auto',
                '&:hover': { bgcolor: brandColors.dark }
              }}>
                <AddIcon />
              </Fab>
            )}
          </Box>
          <Grid container spacing={3} sx={{width:'100%', height:'100%'}}>
            {pageData.coreValues.map((value, index) => (
              <Grid item xs={12} md={6} lg={4} key={index} sx={{width:'100%', height:'100%'}}>
                <ValueCard 
                  value={value} 
                  index={index} 
                  type="coreValues" 
                  brandColors={brandColors} 
                  editMode={editMode} 
                  handleDelete={handleDelete} 
                  handleEdit={handleEdit} 
                  
                />
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Why Join Us */}
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" mb={3} sx={{ gap: '10px' }}>
            <Typography variant="h4" sx={{ color: brandColors.dark, fontWeight: 600 }}>
              Why Join Marabes Group?
            </Typography>
            {editMode && (
              <Fab size="small" onClick={() => handleEdit('whyJoin')} sx={{ 
                bgcolor: brandColors.primary,
                width: 32, height: 32,
                minWidth: 32, minHeight: 32,
                fontSize: 12, marginLeft: 'auto',
                '&:hover': { bgcolor: brandColors.dark }
              }}>
                <AddIcon />
              </Fab>
            )}
          </Box>
          <Grid container spacing={3}>
            {pageData.whyJoin.map((item, index) => (
              <Grid item xs={12} md={6} key={index}sx={{width:'100%', height:'100%'}}>
                <ValueCard 
                  value={item} 
                  index={index} 
                  type="whyJoin" 
                  brandColors={brandColors} 
                  editMode={editMode} 
                  handleDelete={handleDelete} 
                  handleEdit={handleEdit}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>

        <RestrictedToRole allowedRoles={['DIRECTOR', 'MANAGER', 'HR']}>
        {/* Edit Mode Controls */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
          {editMode ? (
            <>
              <Button 
                onClick={handleCancelEdit}
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveChanges}
                variant="contained"
                color="primary"
                startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={isSaving}
                sx={{ bgcolor: brandColors.primary, '&:hover': { bgcolor: brandColors.dark } }}
              >
                {isSaving ? 'Saving...' : 'Save All Changes'}
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => setEditMode(true)}
              variant="contained"
              color="primary"
              sx={{ bgcolor: brandColors.primary, '&:hover': { bgcolor: brandColors.dark } }}
            >
              Enter Edit Mode
            </Button>
          )}
        </Box>
        </RestrictedToRole>
      </Container>
      

      {/* Edit Item Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, type: '', index: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          {editDialog.index === null ? 'Add New' : 'Edit'} 
          <IconButton onClick={() => setEditDialog({ open: false, type: '', index: null })} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {(editDialog.type === 'coreValues' || editDialog.type === 'whyJoin') && (
            <TextField
              fullWidth
              label="Title"
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.target.value)}
              variant="outlined"
              sx={{ mb: 2, mt: 1 }}
            />
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Content"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, type: '', index: null })} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={isSaving}
            sx={{ bgcolor: brandColors.primary, '&:hover': { bgcolor: brandColors.dark } }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarabesAboutPage;